import { getSupplierGenericProperties } from '@bridebook/web/app-basic/lib/analytics/helper';
import { clickedOnLoggedOutPage } from '@bridebook/web/app-basic/lib/analytics/tracking-actions/logged-out-page-click';
import {
  getPremium,
  getSpecialOffers,
  getSupplier,
  getSupplierTier,
} from '@bridebook/web/app-basic/lib/supplier/selectors';
import { useSelector } from '@bridebook/web/app-basic/lib/utils';
import { useCallback, useMemo } from 'react';
import { useDispatch } from 'react-redux';
import type { IOffer } from '@bridebook/models/source/models/Suppliers/Offers.types';
import SignupLink from '@bridebook/web/app-shared/components/signup-link/signup-link';
import { useMarket } from '@bridebook/web/app-shared/lib/i18n/hooks/use-market';
import { OfferTypes } from '@bridebook/web/lib/offers/constants';
import { Box, Slider } from '@bridebook/ui';
import type { Props as AliceCarouselProps } from 'react-alice-carousel/lib/types';
import screens from '@bridebook/ui/src/themes/variables/screens';
import OfferTile from '@bridebook/web/app-shared/components/supplier/sections/offers/tile';
import { sortOffersByType } from '@bridebook/web/app-shared/components/supplier/sections/offers/utils';
import componentStyles from './special-offers.style';
import { useTranslation } from 'next-i18next';
import { PremiumTiers } from '@bridebook/models/source/models/Suppliers/Admin/admin.types';

const sliderProps: AliceCarouselProps = {
  infinite: false,
  responsive: {
    [screens.desktopSm]: {
      items: 2.3,
    },
    [screens.mobileSm]: {
      items: 1.2,
    },
  },
  autoWidth: false,
  disableButtonsControls: false,
  disableDotsControls: true,
};

const SpecialOffers = () => {
  const { t } = useTranslation('supplier');

  const specialOffers = useSelector(getSpecialOffers);
  const premium = useSelector(getPremium);
  const market = useMarket();
  const dispatch = useDispatch();
  const supplier = useSelector(getSupplier);
  const supplierTier = useSelector(getSupplierTier);

  const currencySymbol = market.getCurrencySymbol();

  const styles = componentStyles();

  const supplierGenericProps = useMemo(
    () => getSupplierGenericProperties({ supplier, supplierTier }),
    [supplier, supplierTier],
  );

  const onTitleClick = useCallback(
    (_: IOffer, type: OfferTypes) => {
      dispatch(
        clickedOnLoggedOutPage({
          clickedLocation: 'supplierProfile',
          clickedSection: 'specialOffers',
          cta: type,
          ...supplierGenericProps,
        }),
      );
    },
    [dispatch, supplierGenericProps],
  );

  const commonProps = useMemo(
    () => ({
      currencySymbol,
      onTileClick: onTitleClick,
    }),
    [currencySymbol, onTitleClick],
  );

  const renderOffers = useMemo(
    () =>
      specialOffers
        ?.filter((o) => o && o.type)
        .sort(sortOffersByType)
        .map((offer) => (
          <SignupLink noAnalytics style={styles.item} key={`offer-${offer.id}`}>
            <OfferTile type={offer.type || OfferTypes.custom} offer={offer} {...commonProps} />
          </SignupLink>
        )) || [],
    [commonProps, specialOffers, styles],
  );

  if (!currencySymbol || renderOffers.length === 0 || (premium?.tier ?? -1) < PremiumTiers.Tier_0) {
    return null;
  }

  return (
    <Box style={styles.wrapper}>
      <Box as="h5" style={styles.title}>
        {`${t('limitedTimeOffersAndDeals')} (${renderOffers.length})`}
      </Box>
      <Slider showControlsOnHover sliderProps={sliderProps} items={renderOffers} />
    </Box>
  );
};

export default SpecialOffers;
